"use client"

import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  Box,
  CircularProgress,
  Button,
  Paper,
  Typography,
  Divider,
  Grid,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material"
import { ArrowBack, Delete, Check } from "@mui/icons-material"
import { useNotification } from "../../contexts/NotificationContext"
import { useAuth } from "../../contexts/AuthContext"
import PageHeader from "../../components/common/PageHeader"
import PlanForm from "../../components/plans/PlanForm"
import { getPlan, updatePlan, deletePlan } from "../../services/subscription.service"

const PlanDetails = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { showNotification } = useNotification()
  const { user } = useAuth()
  const [plan, setPlan] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [editMode, setEditMode] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  useEffect(() => {
    fetchPlan()
  }, [id])

  const fetchPlan = async () => {
    setLoading(true)
    try {
      const response = await getPlan(id)
      setPlan(response.data)
    } catch (error) {
      showNotification("Failed to fetch plan details", "error")
      navigate("/plans")
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = () => {
    setEditMode(true)
  }

  const handleCancelEdit = () => {
    setEditMode(false)
  }

  const handleUpdate = async (formData) => {
    setSaving(true)
    try {
      const { masterPassword, ...planData } = formData
      const response = await updatePlan(id, planData, masterPassword)
      setPlan(response.data)
      showNotification("Plan updated successfully", "success")
      setEditMode(false)
    } catch (error) {
      showNotification(error.response?.data?.message || "Failed to update plan", "error")
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    try {
      await deletePlan(id)
      showNotification("Plan deleted successfully", "success")
      navigate("/plans")
    } catch (error) {
      showNotification(error.response?.data?.message || "Failed to delete plan", "error")
    } finally {
      setDeleteDialogOpen(false)
    }
  }

  if (loading || !plan) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <PageHeader
        title={plan.name}
        subtitle="Subscription Plan Details"
        breadcrumbs={[
          { text: "Dashboard", link: "/dashboard" },
          { text: "Plans", link: "/plans" },
          { text: plan.name },
        ]}
        action
        actionText="Delete Plan"
        actionIcon={<Delete />}
        actionColor="error"
        onActionClick={() => setDeleteDialogOpen(true)}
      />

      <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate("/plans")} sx={{ mb: 3 }}>
        Back to Plans
      </Button>

      {!editMode ? (
        <>
          <Box sx={{ mb: 3 }}>
            <Button variant="contained" onClick={handleEdit}>
              Edit Plan
            </Button>
          </Box>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography variant="h5">{plan.name}</Typography>
              <Box>
                {plan.planCategory && (
                  <Chip 
                    label={plan.planCategory === 'friends_family' ? 'Friends & Family' : plan.planCategory}
                    color={plan.planCategory === 'unlimited' ? 'warning' : (plan.planCategory === 'friends_family' ? 'secondary' : 'default')}
                    sx={{ mr: 1 }}
                  />
                )}
                <Chip 
                  label={plan.isActive ? "Active" : "Inactive"} 
                  color={plan.isActive ? "success" : "default"}
                  sx={{ mr: 1 }}
                />
                <Chip 
                  label={plan.isPublic ? "Public" : "Private"} 
                  color={plan.isPublic ? "info" : "default"}
                  sx={{ mr: 1 }}
                />
                {plan.isDefault && (
                  <Chip
                    label="Default Plan"
                    color="info"
                  />
                )}
              </Box>
            </Box>
            <Typography variant="body1" sx={{ mb: 3 }}>
              {plan.description}
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: "100%" }}>
                  <Typography variant="h6" gutterBottom>
                    Pricing
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  {plan.planType === "lifetime" ? (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Lifetime Price
                        </Typography>
                        <Typography variant="h5">
                          {plan.lifetimePrice && plan.lifetimePrice > 0
                            ? `$${plan.lifetimePrice} one-time`
                            : "Included with license"}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Currency
                        </Typography>
                        <Typography variant="body1">{plan.currency}</Typography>
                      </Box>
                    </>
                  ) : plan.planType === "free" ? (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Price
                        </Typography>
                        <Typography variant="h5">Free</Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Currency
                        </Typography>
                        <Typography variant="body1">{plan.currency}</Typography>
                      </Box>
                    </>
                  ) : (
                    <>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Monthly Price
                        </Typography>
                        <Typography variant="h5">${plan.monthlyPrice}</Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Annual Price
                        </Typography>
                        <Typography variant="h5">${plan.annualPrice}</Typography>
                      </Box>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Currency
                        </Typography>
                        <Typography variant="body1">{plan.currency}</Typography>
                      </Box>
                      {plan.trialDays > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" color="text.secondary">
                            Trial Period
                          </Typography>
                          <Typography variant="body1">{plan.trialDays} days</Typography>
                        </Box>
                      )}
                    </>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, height: "100%" }}>
                  <Typography variant="h6" gutterBottom>
                    Limits & Allowances
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Credit Allowance
                    </Typography>
                    <Typography variant="body1">{plan.creditAllowance} credits</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Maximum Users
                    </Typography>
                    <Typography variant="body1">{plan.maxUsers} users</Typography>
                  </Box>
                  {plan.entitlements && typeof plan.entitlements.executionHistoryDays === "number" && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Execution History
                      </Typography>
                      <Typography variant="body1">
                        {plan.entitlements.executionHistoryDays} days
                      </Typography>
                    </Box>
                  )}
                  {plan.entitlements && (typeof plan.entitlements.maxFileSizeMb === "number" || plan.entitlements.maxFileSizeMb === null) && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Maximum File Size
                      </Typography>
                      <Typography variant="body1">
                        {plan.entitlements.maxFileSizeMb === null
                          ? "Custom"
                          : `${plan.entitlements.maxFileSizeMb} MB`}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Sort Order
                    </Typography>
                    <Typography variant="body1">{plan.sortOrder}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Tier Level
                    </Typography>
                    <Typography variant="body1">{plan.tier_level || 0}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Higher tier level indicates more premium plan
                    </Typography>
                  </Box>
                  {plan.entitlements && (
                    <Box sx={{ mt: 2 }}>
                      {typeof plan.entitlements.customAiAgentCreation === "boolean" && (
                        <Typography variant="body2" color="text.secondary">
                          Custom AI Agent Creation:{" "}
                          {plan.entitlements.customAiAgentCreation ? "Enabled" : "Disabled"}
                        </Typography>
                      )}
                      {typeof plan.entitlements.operationsUsageFlexibility === "boolean" && (
                        <Typography variant="body2" color="text.secondary">
                          Operations Usage Flexibility:{" "}
                          {plan.entitlements.operationsUsageFlexibility ? "Enabled" : "Disabled"}
                        </Typography>
                      )}
                      {typeof plan.entitlements.removeBranding === "boolean" && (
                        <Typography variant="body2" color="text.secondary">
                          Remove TinyCommand Branding:{" "}
                          {plan.entitlements.removeBranding ? "Enabled" : "Disabled"}
                        </Typography>
                      )}
                      {typeof plan.entitlements.folderFilePermissions === "boolean" && (
                        <Typography variant="body2" color="text.secondary">
                          Folder/File Permissions:{" "}
                          {plan.entitlements.folderFilePermissions ? "Enabled" : "Disabled"}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Features
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {Array.isArray(plan.features) && plan.features.length > 0 ? (
                    <List>
                      {plan.features.map((feature, index) => (
                        <ListItem key={index}>
                          <ListItemIcon>
                            <Check color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                      No features specified
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        </>
      ) : (
        <Box>
          <Button variant="outlined" onClick={handleCancelEdit} sx={{ mb: 3 }}>
            Cancel Editing
          </Button>
          <PlanForm plan={plan} onSubmit={handleUpdate} loading={saving} isEdit={true} />
        </Box>
      )}

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Plan</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the plan "{plan.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default PlanDetails

